
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Rocket, ShieldCheck, Zap, X, ChevronRight, Check } from 'lucide-react';
import { Plan, PLANS } from '../lib/pricing';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (plan: Plan) => void;
  trigger: 'generation_limit' | 'deploy_block' | 'project_limit' | null;
  appPreview?: string;
  readiness?: number;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ 
  isOpen, 
  onClose, 
  onUpgrade, 
  trigger,
  appPreview,
  readiness = 82
}) => {
  if (!isOpen) return null;

  const founderPlan = PLANS.founder;
  const proPlan = PLANS.pro;

  const getHeadline = () => {
    switch (trigger) {
      case 'deploy_block':
        return "You're 1 step away from shipping this app";
      case 'generation_limit':
        return "Unlock unlimited builds to finish your vision";
      case 'project_limit':
        return "Expand your workspace for new ideas";
      default:
        return "Take your app to the next level";
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-4xl bg-[#0A0A0A] border border-[#222] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row min-h-[500px]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Left Side: Context & Preview */}
          <div className="hidden md:flex md:w-5/12 bg-[#050505] p-8 flex-col relative overflow-hidden border-r border-[#151515]">
            <div className="relative z-10">
              <div className="text-[10px] font-mono text-[#555] uppercase tracking-[0.2em] mb-4">Current Synthesis</div>
              <h3 className="text-xl font-medium text-white mb-2 leading-tight">
                {getHeadline()}
              </h3>
              
              <div className="mt-8 space-y-4">
                <div className="p-4 bg-[#111] border border-[#222] rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-mono text-[#555] uppercase">App Readiness</span>
                    <span className="text-[10px] font-mono text-[#0074D9]">{readiness}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-[#050505] rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${readiness}%` }}
                      className="h-full bg-[#0074D9]"
                    />
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {[
                    "Deploy your app live",
                    "Unlock unlimited builds",
                    "Save and iterate anytime"
                  ].map((text, i) => (
                    <li key={i} className="flex items-center gap-3 text-xs text-[#888]">
                      <div className="w-4 h-4 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                        <Check size={10} className="text-green-500" />
                      </div>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* App Preview Background */}
            <div className="absolute bottom-0 left-0 right-0 h-1/2 opacity-20 pointer-events-none">
              {appPreview && (
                <iframe 
                  srcDoc={appPreview}
                  className="w-[1000px] h-[1000px] scale-[0.3] origin-bottom-left border-none opacity-40 grayscale"
                  title="App background preview"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
            
            <div className="mt-auto relative z-10 pt-8 border-t border-[#151515]">
              <div className="flex items-center gap-2 text-[10px] font-mono text-[#444]">
                <ShieldCheck size={12} />
                SECURE CHECKOUT VIA STRIPE
              </div>
            </div>
          </div>

          {/* Right Side: Options */}
          <div className="flex-1 p-8 flex flex-col justify-center">
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 p-2 text-[#444] hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="max-w-md mx-auto w-full">
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-2">Ready to ship?</h2>
                <p className="text-sm text-[#888]">Choose the plan that fits your ambition.</p>
              </div>

              <div className="space-y-4">
                {/* Founder Plan - Most Important */}
                <button
                  onClick={() => onUpgrade('founder')}
                  className="w-full group relative p-6 bg-[#111] border-2 border-[#0074D9] rounded-2xl text-left hover:bg-[#151515] transition-all transform hover:scale-[1.02]"
                >
                  <div className="absolute -top-3 right-6 px-3 py-1 bg-[#0074D9] text-[9px] font-mono font-bold text-white rounded-full uppercase tracking-widest shadow-lg">
                    Recommended
                  </div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-semibold text-lg">{founderPlan.name}</h4>
                      <p className="text-xs text-[#666]">Best for first real app</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">{founderPlan.monthlyPriceLabel}</div>
                      <div className="text-[10px] text-[#555] font-mono uppercase">First Month</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs text-[#888]">
                      <Zap size={14} className="text-[#0074D9]" /> 
                      Unlimited AI generations
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#888]">
                      <Rocket size={14} className="text-[#0074D9]" /> 
                      Deploy your first app
                    </div>
                  </div>

                  <div className="w-full py-3 bg-[#0074D9] text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-2 group-hover:bg-[#0088ff] transition-colors">
                    Finish this app <ChevronRight size={16} />
                  </div>
                </button>

                {/* Pro Plan */}
                <button
                  onClick={() => onUpgrade('pro')}
                  className="w-full p-6 bg-[#0A0A0A] border border-[#222] rounded-2xl text-left hover:border-[#444] transition-all"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-white font-medium">{proPlan.name}</h4>
                    <div className="text-lg font-bold text-white">{proPlan.monthlyPriceLabel}<span className="text-xs font-normal text-[#555]">/mo</span></div>
                  </div>
                  <p className="text-xs text-[#666] mb-4">For serious builders who need total freedom.</p>
                  <div className="flex items-center gap-2 text-[10px] text-[#0074D9] font-mono uppercase tracking-widest">
                    Remove all limits <ChevronRight size={12} />
                  </div>
                </button>
              </div>

              <div className="mt-8 text-center text-xs text-[#444]">
                <button 
                  onClick={onClose}
                  className="hover:text-[#888] transition-colors underline underline-offset-4"
                >
                  Continue exploring (limited)
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
